import { SessionRepository } from './store';
import { Url, UrlParam, UrlValue } from '../../core/url';
import { LocalStorage } from '../../core/local-storage';
import {
  SessionContext,
  SessionCreateBody,
  SessionDisplay,
  SessionHelper,
  SessionModel,
  SessionSorting,
  SessionSortType,
} from './model';
import { Http, OnErrorData } from '../../core/http';
import { catchError, share, switchMap } from 'rxjs/operators';
import { SessionApi } from './session.api';
import { SessionService } from './session.service';
import { BasketService } from '../basket';
import { Observable, of, throwError, zip } from 'rxjs';
import * as Cookies from 'js-cookie';
import { Env } from '../../env';

let fetchUpdateSessionSorting$: Observable<void>;

const setLoadingInSession = (state: boolean) =>
  SessionRepository.setLoadingInSession(state);

const getSessionIdFromStorage = (paramsSession: UrlParam[UrlValue.S]) => {
  try {
    return JSON.parse(paramsSession);
  } catch (e) {
    return undefined;
  }
};

const getSessionIdFromParams = () => {
  const params = Url.getParamsInQueryString();
  return getSessionIdFromStorage(params.fusionId);
};

const getSessionFromLocalStorage = () => {
  const localStorageSessionString = LocalStorage.get(UrlValue.S);
  return localStorageSessionString != null
    ? getSessionIdFromStorage(localStorageSessionString)
    : null;
};

const getSessionFromSessionStorage = () => {
  return sessionStorage.getItem(UrlValue.S);
};

const removeSessionIdFromCookie = () => {
  for (let i = 0; i < 10; i++) {
    document.cookie = 'fusionId= ; expires = Thu, 01 Jan 1970 00:00:00 GMT';
  }
  Cookies.remove('fusionId');
};

const storeSession = (session: SessionModel) => {
  SessionRepository.updateSession(session);
  window.QW_HOTEL_ENV.ALLOW_QUERY_STRING_SESSION_ID &&
    Url.setParamInQueryString(UrlValue.S, JSON.stringify(session.sessionId));
  window.QW_HOTEL_ENV.USE_SESSION_STORAGE
    ? sessionStorage.setItem(UrlValue.S, session.sessionId)
    : LocalStorage.set<string>(UrlValue.S, session.sessionId);
  setLoadingInSession(false);
};

const onErrorInvalidSession = (err: OnErrorData) => {
  setLoadingInSession(false);
  if (isInvalidSession(err)) {
    return createNewSession();
  }
  return Http.error(err);
};

const isInvalidSession = (err: OnErrorData) => {
  return (
    Http.isInvalidSessionResponse(err) || Http.isNotAcceptableResponse(err)
  );
};

const fetchSessionAndHandleInvalidError = (id: SessionModel['sessionId']) => {
  return SessionService.fetchSession(id).pipe(
    catchError((err) => {
      return onErrorInvalidSession(err);
    }),
  );
};

const fetchUpdateSessionContext = (
  sessionId: SessionModel['sessionId'],
  body: SessionContext,
) => {
  const updatedBody = window.QW_HOTEL_ENV.USE_SESSION_STORAGE
    ? SessionHelper.addPromoCodeFromSessionStorage(body)
    : SessionHelper.addPromoCodeFromLocalStorage(body);
  return Http.patch<void, SessionContext>(
    SessionApi.updateContext(sessionId),
    updatedBody as SessionContext,
  );
};

const fetchUpdateSessionDisplay = (
  sessionId: SessionModel['sessionId'],
  body: SessionDisplay,
) => {
  return Http.patch<void, SessionDisplay>(
    SessionApi.updateDisplay(sessionId),
    body,
  );
};

const fetchUpdateSessionSorting = (
  sessionId: SessionModel['sessionId'],
  body: SessionSorting,
) => {
  if (!fetchUpdateSessionSorting$) {
    fetchUpdateSessionSorting$ = Http.patch<void, SessionSorting>(
      SessionApi.updateSorting(sessionId),
      body,
    ).pipe(share());
  }

  return fetchUpdateSessionSorting$;
};

const fetchUpdateSessionContextWithNotEmptyBasket = (
  body: SessionContext,
  session: SessionModel,
) => {
  return BasketService.fetchDeleteBasket(session.sessionId).pipe(
    switchMap(() => fetchUpdateSessionContext(session.sessionId, body)),
    switchMap(() => BasketService.fetchBasket(session)),
  );
};

const createNewSession = (
  body: Partial<SessionCreateBody> = {},
): Observable<SessionModel> => {
  const updatedContex = window.QW_HOTEL_ENV.USE_SESSION_STORAGE
    ? SessionHelper.addPromoCodeFromSessionStorage(body.context)
    : SessionHelper.addPromoCodeFromLocalStorage(body.context);
  const updateDisplay = {
    currency: window.QW_HOTEL_ENV.CURRENCY || Env.Currency,
    countryCode: Env.Country,
    culture: Env.DefaultLanguage,
  };
  const updatedBody = {
    ...body,
    context: updatedContex,
    display: updateDisplay,
  } as SessionCreateBody;
  setLoadingInSession(true);
  return SessionService.fetchCreateSession(updatedBody).pipe(
    switchMap((sessionCreated) => {
      return zip(
        of(sessionCreated.sessionId),
        fetchUpdateSessionSorting(sessionCreated.sessionId, {
          sortType: SessionSortType.AscendingPrice,
        }),
      );
    }),
    switchMap(([sessionId]) => SessionService.fetchSession(sessionId)),
    catchError((err) => throwError(err)),
  );
};

export const SessionHelperService = {
  setLoadingInSession,
  getSessionIdFromParams,
  getSessionFromLocalStorage,
  getSessionFromSessionStorage,
  removeSessionIdFromCookie,
  storeSession,
  fetchSessionAndHandleInvalidError,
  fetchUpdateSessionContext,
  fetchUpdateSessionContextWithNotEmptyBasket,
  fetchUpdateSessionDisplay,
  createNewSession,
  isInvalidSession,
};
